import 'monaco-editor/esm/vs/base/browser/ui/actionbar/actionbar.css';
import 'graphiql/graphiql.min.css';
import 'antd/dist/antd.css'; // or 'antd/dist/antd.less'
import '@cognite/cogs.js/dist/cogs.css';
import { FlagProvider } from '@cognite/react-feature-flags';
import { ThemeProvider } from 'styled-components/macro';
import { GlobalStyle, theme } from 'styles/theme';
import { StyledPage, StyledWrapper } from './App.styles';
import { StoreProvider } from 'easy-peasy';
import { Header, InfoMessage } from 'components';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  useApolloClient,
} from '@apollo/client';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import React, { useEffect } from 'react';
import { LoginPage } from 'pages/LoginPage/LoginPage';
import { useStoreState } from 'hooks';
import { AllDigitalTwinsPage } from 'pages/AllDigitalTwinsPage/AllDigitalTwinsPage';
import { ViewDigitalTwinPage } from 'pages/ViewDigitalTwinPage/ViewDigitalTwinPage';
import { useUrl } from 'hooks/useUrls';
import { Button, Loader } from '@cognite/cogs.js';
import { QueryClient, QueryClientProvider } from 'react-query';
import store from 'store';
import { getITGInterceptor } from 'utils/graphql-url';
import { MsalProvider } from '@azure/msal-react';
import {
  HOME_DIGITAL_TWIN,
  ROUTE_ACCESS_CONTROL,
  ROUTE_DIGITAL_TWIN,
} from 'utils/router-url';
import { UNLEASH_FLAG_PROVIDER_API_TOKEN } from 'utils/config';
import { useApolloAuthLink } from 'hooks/useGraphQL';
import { publicClient, useLogin } from 'store/auth';
import { useQuery } from 'hooks/useQuery';
import { AccessControl } from 'pages/AccessControl/AccessControl';
import { notification } from 'antd';
import { useStatefulHistory } from './hooks/history';

const itgLink = new HttpLink({ fetch: getITGInterceptor });

const client = new ApolloClient({
  link: itgLink, // Set initial link so queries can execute
  cache: new InMemoryCache({
    // Apollo automatically adds __typename to queries. This was causing
    // a bug when using orderBy. See https://github.com/neo4j-graphql/neo4j-graphql-js/issues/581
    addTypename: false,
  }),
  // React 18 StrictMode compatibility
  defaultOptions: {
    watchQuery: {
      // Use cache-and-network to fetch data on first load and use cache on subsequent remounts
      fetchPolicy: 'cache-and-network',
      // After first fetch, use cache-first to prevent duplicate queries in StrictMode
      nextFetchPolicy: 'cache-first',
    },
  },
});

const queryClient = new QueryClient();

const FlagProviderSetup = ({ children }: { children: React.ReactNode }) => {
  const tenant = useStoreState((state) => state.auth.tenant);
  return (
    <FlagProvider
      projectName={tenant}
      appName="Galactica"
      apiToken={UNLEASH_FLAG_PROVIDER_API_TOKEN}
      remoteAddress={window.location.hostname}
      refreshInterval={86400}
      proxyUrl="https://unleash-edge.cogniteapp.com/api/proxy"
    >
      {children}
    </FlagProvider>
  );
};

export default function MyApp() {
  return (
    <>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <MsalProvider instance={publicClient}>
            <StoreProvider store={store}>
              <ApolloProvider client={client}>
                <FlagProviderSetup>
                  <Router>
                    <StyledPage>
                      <Switch>
                        <Route exact path="/">
                          <LoginPage />
                        </Route>
                        <Route path="/:tenantId">
                          <RedirectToUrlTenant>
                            <StyledWrapper>
                              <Switch>
                                <Route exact path="/:tenantId">
                                  <Header />
                                  <AllDigitalTwinsPage />
                                </Route>
                                <Route exact path={ROUTE_ACCESS_CONTROL}>
                                  <Header />
                                  <AccessControl />
                                </Route>
                                <Route exact path={HOME_DIGITAL_TWIN}>
                                  <Header />
                                  <AllDigitalTwinsPage />
                                </Route>
                                <Route path={ROUTE_DIGITAL_TWIN}>
                                  <Header />
                                  <ViewDigitalTwinPage />
                                </Route>
                              </Switch>
                            </StyledWrapper>
                          </RedirectToUrlTenant>
                        </Route>
                      </Switch>
                    </StyledPage>
                  </Router>
                </FlagProviderSetup>
              </ApolloProvider>
            </StoreProvider>
          </MsalProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </>
  );
}

const RedirectToUrlTenant = ({
  children = null,
}: {
  children: React.ReactNode;
}) => {
  const isSignedIn = useStoreState((state) => state.auth.loggedIn);
  const tenant = useStoreState((state) => state.auth.tenant);
  const apiKey = useStoreState((state) => state.auth.apiKey);
  const login = useLogin();
  const urlTenant = useUrl.Tenant();
  const apolloAuthLink = useApolloAuthLink();
  const apolloClient = useApolloClient();

  const method = useQuery.Method();
  const history = useStatefulHistory();

  useEffect(() => {
    // when the url tenant and the stored tenant is not the same, try to login with oauth
    if (tenant !== urlTenant) {
      login({ tenant: urlTenant, useOIDC: method === 'oauth' }).catch(() => {
        notification.error({ message: 'Failed to perform login request' });
      });
    }
  }, [tenant, urlTenant, login]);

  useEffect(() => {
    // when the url tenant and the stored tenant is not the same, try to login with oauth
    if (isSignedIn) {
      apolloClient.setLink(ApolloLink.from([apolloAuthLink, itgLink]));
      // Don't refetch immediately - let queries run naturally with proper skip conditions
      // This prevents refetching queries that might have null IDs
    }
  }, [apolloClient, apolloAuthLink, isSignedIn]);

  useEffect(() => {
    // first time loading back into `tenant`
    try {
      if (isSignedIn === undefined) {
        if (method === 'apikey') {
          login({ apiKey });
        } else if (method === 'oauth' && tenant === urlTenant) {
          login({ tenant, useOIDC: true });
        }
      }
    } catch (e) {
      history.push('/');
    }
  }, [isSignedIn, login, method]);

  if (!method) {
    return (
      <>
        <InfoMessage type="Error" title="Unable to log in">
          <>
            <p>Unable to determine your login method, please sign in again</p>
            <Button
              icon="ArrowRight"
              iconPlacement="right"
              type="primary"
              onClick={() => history.push('/')}
            >
              Sign in again
            </Button>
          </>
        </InfoMessage>
      </>
    );
  }

  if (isSignedIn === undefined) {
    return <Loader darkMode />;
  }
  return <>{children}</>;
};
